import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowUpRight, Mail, MapPin, MessageCircle, Send, Users } from "lucide-react";
import { site } from "@/content/site";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";

const schema = z.object({
  name: z.string().min(2, "Please enter your name (min. 2 characters).").max(100),
  email: z.string().email("Please enter a valid email address."),
  subject: z.string().min(3, "Subject needs at least 3 characters.").max(150),
  message: z.string().min(10, "Message needs at least 10 characters.").max(5000),
  company: z.string().max(0).optional(),
});

type Values = z.infer<typeof schema>;

type Status =
  | { state: "idle" }
  | { state: "sending" }
  | { state: "success" }
  | { state: "setup" }
  | { state: "error"; msg: string };

export default function Contact() {
  const [status, setStatus] = useState<Status>({ state: "idle" });
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Values>({ resolver: zodResolver(schema) });

  const onSubmit = async (values: Values) => {
    setStatus({ state: "sending" });
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      if (res.ok) {
        setStatus({ state: "success" });
        reset();
        return;
      }
      if (res.status === 503) {
        setStatus({ state: "setup" });
        return;
      }
      if (res.status === 429) {
        setStatus({
          state: "error",
          msg: "You just sent a message. Please wait a moment before sending another one.",
        });
        return;
      }
      setStatus({ state: "error", msg: "Something went wrong. Please try again in a moment." });
    } catch {
      setStatus({ state: "error", msg: "Network error — please check your connection and try again." });
    }
  };

  const socials = site.socials.filter((s) => s.url);
  const whatsappLink = site.contact.whatsapp ? `https://wa.me/${site.contact.whatsapp}` : null;

  const pending = (label: string) => (
    <span
      className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/30"
      title="Configure this in content settings — nothing is fabricated"
    >
      {label} — pending
    </span>
  );

  return (
    <>
      <SEO
        title="Contact | EVE Band — Booking & Management"
        description="Let's connect — contact EVE band management for bookings, collaborations, media inquiries and partnerships. Modern rock band based in Surabaya, Indonesia."
      />
      <section data-testid="contact-page" className="pb-24 pt-32 lg:pb-32 lg:pt-40">
        <div className="container-eve">
          <Reveal>
            <p className="eyebrow mb-6">Contact</p>
            <h1 className="font-display text-5xl uppercase leading-[0.95] sm:text-6xl lg:text-7xl">
              Let&apos;s <span className="gradient-text">connect</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-eve-silver sm:text-lg">
              Bookings, collaborations, media inquiries and partnerships — we&apos;d love to hear
              from you.
            </p>
          </Reveal>

          <div className="mt-16 grid gap-14 lg:grid-cols-2 lg:gap-20">
            {/* Left: info */}
            <Reveal delay={0.05}>
              <div className="space-y-10">
                <div data-testid="contact-block-email">
                  <p className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-eve-cyan">
                    <Mail size={14} /> Booking &amp; management
                  </p>
                  <div className="mt-3">
                    {site.contact.managementEmail ? (
                      <a
                        href={`mailto:${site.contact.managementEmail}`}
                        data-testid="contact-email-link"
                        className="text-lg text-eve-ink transition-colors duration-300 hover:text-eve-cyan"
                      >
                        {site.contact.managementEmail}
                      </a>
                    ) : (
                      pending("Official email")
                    )}
                  </div>
                </div>

                <div data-testid="contact-block-whatsapp">
                  <p className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-eve-cyan">
                    <MessageCircle size={14} /> WhatsApp
                  </p>
                  <div className="mt-3">
                    {whatsappLink ? (
                      <a
                        href={whatsappLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="contact-whatsapp-link"
                        className="text-lg text-eve-ink transition-colors duration-300 hover:text-eve-cyan"
                      >
                        Chat on WhatsApp ↗
                      </a>
                    ) : (
                      pending("Official number")
                    )}
                  </div>
                </div>

                <div data-testid="contact-block-location">
                  <p className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-eve-cyan">
                    <MapPin size={14} /> Location
                  </p>
                  <p className="mt-3 text-lg text-eve-ink">{site.band.location}</p>
                </div>

                <div data-testid="contact-block-socials">
                  <p className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-eve-cyan">
                    <Users size={14} /> Follow EVE
                  </p>
                  <ul className="mt-4 space-y-2.5">
                    {socials.map((s) => (
                      <li key={s.name}>
                        <a
                          href={s.url as string}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`contact-social-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
                          className="inline-flex items-center gap-1.5 text-sm uppercase tracking-[0.2em] text-eve-silver transition-colors duration-300 hover:text-eve-cyan"
                        >
                          {s.name} <ArrowUpRight size={12} />
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>

            {/* Right: form */}
            <Reveal delay={0.1}>
              <form
                onSubmit={handleSubmit(onSubmit)}
                noValidate
                data-testid="contact-form"
                className="border border-white/10 bg-eve-surface/60 p-8 sm:p-10"
              >
                <div className="space-y-6">
                  <div>
                    <label htmlFor="name" className="field-label">Name</label>
                    <input
                      id="name"
                      type="text"
                      autoComplete="name"
                      placeholder="Your name"
                      data-testid="contact-name-input"
                      className="field-input"
                      {...register("name")}
                    />
                    {errors.name && (
                      <p data-testid="contact-name-error" className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-eve-crimson">
                        {errors.name.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="email" className="field-label">Email</label>
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      placeholder="you@example.com"
                      data-testid="contact-email-input"
                      className="field-input"
                      {...register("email")}
                    />
                    {errors.email && (
                      <p data-testid="contact-email-error" className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-eve-crimson">
                        {errors.email.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="subject" className="field-label">Subject</label>
                    <input
                      id="subject"
                      type="text"
                      placeholder="Booking, media, collaboration…"
                      data-testid="contact-subject-input"
                      className="field-input"
                      {...register("subject")}
                    />
                    {errors.subject && (
                      <p data-testid="contact-subject-error" className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-eve-crimson">
                        {errors.subject.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="message" className="field-label">Message</label>
                    <textarea
                      id="message"
                      rows={5}
                      placeholder="Tell us about your event or inquiry"
                      data-testid="contact-message-input"
                      className="field-input resize-y"
                      {...register("message")}
                    />
                    {errors.message && (
                      <p data-testid="contact-message-error" className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-eve-crimson">
                        {errors.message.message}
                      </p>
                    )}
                  </div>

                  {/* Honeypot — hidden from real users */}
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="hidden"
                    data-testid="contact-company-honeypot"
                    {...register("company")}
                  />

                  <button
                    type="submit"
                    disabled={status.state === "sending"}
                    data-testid="contact-submit-button"
                    className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {status.state === "sending" ? "Sending…" : "Send message"} <Send size={13} />
                  </button>
                </div>

                <div aria-live="polite" className="mt-6">
                  {status.state === "success" && (
                    <p data-testid="contact-status-success" className="border border-eve-cyan/40 bg-eve-cyan/[0.06] px-4 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-eve-cyan">
                      Message sent — EVE management will get back to you soon.
                    </p>
                  )}
                  {status.state === "setup" && (
                    <p data-testid="contact-status-setup" className="border border-eve-violet/40 bg-eve-violet/[0.06] px-4 py-3 font-mono text-[11px] uppercase leading-relaxed tracking-[0.15em] text-eve-silver">
                      Email delivery is not configured yet — messages cannot go out until EVE&apos;s
                      official management inbox is connected. Nothing was sent.
                    </p>
                  )}
                  {status.state === "error" && (
                    <p data-testid="contact-status-error" className="border border-eve-crimson/40 bg-eve-crimson/[0.06] px-4 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-eve-crimson">
                      {status.msg}
                    </p>
                  )}
                </div>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
